import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled, { css } from 'styled-components';
import {
  createClearingAutomationSetting,
  deleteClearingAutomationSetting,
  getClearingAutomationSetting,
  updateClearingAutomationSetting,
} from '~/javascripts/admin/actions/clearing';
import { useAppSelector } from '~/javascripts/admin/hooks/useApp';
import { selectUserPrivateMoney } from '~/javascripts/admin/reducers/user-private-money';
import Button from '../../../features/common/components/Button';
import CheckBox from '../../atoms/CheckBox';
import FormErrorMessage from '../../atoms/FormErrorMessage';
import Grid from '../../atoms/Grid';
import Label from '../../atoms/Label';
import Required from '../../atoms/Required';
import ToggleSwitch from '../../atoms/ToggleSwitch';
import UserPrivateMoneyNotAllowed from '../../atoms/UserPrivateMoneyNotAllowed';
import BreadCrumb from '../../molecules/BreadCrumb';
import SelectedPrivateMoneyDisplay from '../../organisms/SelectedPrivateMoneyDisplay';

type ClearingAutomationSettingForm = {
  isEnabled: boolean;
  isMidOfMonthSelected: boolean;
  isEndOfMonthSelected: boolean;
  isAutoOffsetEnabled: boolean;
};

const PageWrapper = styled.div``;

const FormGrid = styled(Grid.Container)`
  ${Label} {
    margin-bottom: 0;
    padding: 5px 0;
  }

  ${ToggleSwitch} {
    display: block !important;
  }

  ${Grid.Divider} {
    margin: 12px 0;
  }
`;

const ToggleSwitchLabel = styled(Label)`
  margin-left: 1.25rem;
  color: #6f6f6f !important;
  opacity: 0.6;
`;

const ToggleSwitchWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
`;

const CheckBoxRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const AutoOffsetSection = styled.div`
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #eee;
`;

const AutoOffsetDescription = styled.div`
  color: #6f6f6f;
  font-size: 13px;
  margin-left: 1.25rem;
`;

const ApiResult = styled.div<{ isError: boolean }>`
  ${({ theme, isError }) => css`
    padding: 16px;
    border-radius: 4px;
    background: ${isError ? theme.colors.red['100'] : theme.colors.green['100']};
    margin: 8px 0;
  `}
`;

const CRUMBS = [
  { value: 'ホーム', href: '/', isCurrentPage: false },
  { value: '精算管理', href: '', isCurrentPage: true },
  { value: '精算データ作成履歴', href: '/clearing', isCurrentPage: false },
  { value: '自動作成設定', href: '', isCurrentPage: true },
];

const DEFAULT_SETTINGS = {
  isEnabled: false,
  isMidOfMonthSelected: false,
  isEndOfMonthSelected: false,
  isAutoOffsetEnabled: false,
};

// 精算設定レコードが存在するか。BE は「スケジュールも自動相殺も無い無意味レコード」を
// 弾くため、monthly_settlement_dates が非空 または auto_offset_enabled=true なら必ず実在する。
const settingRecordExists = (setting: {
  monthly_settlement_dates?: number[] | null;
  auto_offset_enabled?: boolean;
}) => (setting.monthly_settlement_dates?.length ?? 0) > 0 || setting.auto_offset_enabled === true;

const initialApiResult = {
  message: '',
  isError: false,
};

const ClearingAutomatedSetting = () => {
  const user = useAppSelector((state) => state.user);
  const { id: privateMoneyId } = useAppSelector(selectUserPrivateMoney);

  const allowedPrivateMoneys = user.issuedThirdPartyMoneys();
  const isSelectedMoneyAllowed = allowedPrivateMoneys.some((m) => m.id === privateMoneyId);

  const [apiResult, setApiResult] = useState(initialApiResult);
  const flashApiResult = () => setTimeout(() => setApiResult(initialApiResult), 6000);

  const {
    reset,
    control,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<ClearingAutomationSettingForm>({
    defaultValues: DEFAULT_SETTINGS,
  });

  useEffect(() => {
    getClearingAutomationSetting(privateMoneyId).then((setting) => {
      const dates = setting.monthly_settlement_dates ?? [];
      reset({
        isEnabled: dates.length > 0,
        isMidOfMonthSelected: dates.includes(15),
        isEndOfMonthSelected: dates.includes(0),
        isAutoOffsetEnabled: setting.auto_offset_enabled === true,
      });
    });
  }, [privateMoneyId, reset]);

  const onUpdateSetting = async (data: ClearingAutomationSettingForm) => {
    const onSuccess = () => {
      setApiResult({ message: '精算設定を登録しました', isError: false });
      flashApiResult();
    };

    // 自動精算スケジュールと自動相殺は独立した設定。両方の希望状態からレコード操作を決める。
    const desiredDates = [
      ...(data.isEndOfMonthSelected ? [0] : []),
      ...(data.isMidOfMonthSelected ? [15] : []),
    ];
    const hasSchedule = data.isEnabled && desiredDates.length > 0;
    const desiredAuto = data.isAutoOffsetEnabled;

    try {
      const currentSetting = await getClearingAutomationSetting(privateMoneyId);
      const recordExists = settingRecordExists(currentSetting);
      const currentHasSchedule = (currentSetting.monthly_settlement_dates?.length ?? 0) > 0;

      // 1. スケジュールも自動相殺も無効 → レコードは持たない（無意味レコード回避）
      if (!hasSchedule && !desiredAuto) {
        if (recordExists) await deleteClearingAutomationSetting(privateMoneyId);
        onSuccess();
        return;
      }

      // 2. レコードが無い → 新規作成
      //    自動相殺のみ有効化する場合は monthly_settlement_dates を「省略」する
      //    （null を送ると BE が空配列とみなし 422 になるため）
      if (!recordExists) {
        await createClearingAutomationSetting(
          privateMoneyId,
          hasSchedule
            ? { monthly_settlement_dates: desiredDates, auto_offset_enabled: desiredAuto }
            : { auto_offset_enabled: true }
        );
        onSuccess();
        return;
      }

      // 3. レコードあり＋スケジュール有効 → 更新（dates と auto をまとめて反映）
      if (hasSchedule) {
        await updateClearingAutomationSetting(privateMoneyId, {
          monthly_settlement_dates: desiredDates,
          auto_offset_enabled: desiredAuto,
        });
        onSuccess();
        return;
      }

      // 4. レコードあり＋自動相殺のみ（スケジュール無効）
      //    既存スケジュールがある場合、PATCH では dates を空に出来ないため作り直す。
      //    DELETE→POST は非原子的なため、POST 失敗時は元のスケジュールを復元する。
      if (currentHasSchedule) {
        await deleteClearingAutomationSetting(privateMoneyId);
        try {
          await createClearingAutomationSetting(privateMoneyId, { auto_offset_enabled: true });
        } catch (e) {
          await createClearingAutomationSetting(privateMoneyId, {
            monthly_settlement_dates: currentSetting.monthly_settlement_dates ?? [],
            auto_offset_enabled: currentSetting.auto_offset_enabled ?? false,
          }).catch(() => {});
          throw e;
        }
      } else {
        await updateClearingAutomationSetting(privateMoneyId, { auto_offset_enabled: true });
      }
      onSuccess();
    } catch (e) {
      setApiResult({
        message: e.message || 'エラー',
        isError: true,
      });
      // 失敗時は実 DB 状態をフォームへ再同期し、表示と DB の乖離を防ぐ。
      getClearingAutomationSetting(privateMoneyId)
        .then((setting) => {
          const dates = setting.monthly_settlement_dates ?? [];
          reset({
            isEnabled: dates.length > 0,
            isMidOfMonthSelected: dates.includes(15),
            isEndOfMonthSelected: dates.includes(0),
            isAutoOffsetEnabled: setting.auto_offset_enabled === true,
          });
        })
        .catch(() => {});
    }
  };

  return (
    <PageWrapper>
      <BreadCrumb crumbs={CRUMBS} />
      {isSelectedMoneyAllowed ? (
        <>
          <SelectedPrivateMoneyDisplay />
          <FormGrid>
            <Grid.Item size={2}>
              <Label>自動作成設定</Label>
            </Grid.Item>
            <Grid.Item size={10} flexDirection="column">
              <ToggleSwitchWrapper>
                <Controller
                  control={control}
                  name="isEnabled"
                  render={({ field: { value, onChange } }) => (
                    <ToggleSwitch
                      checked={value}
                      onChange={onChange}
                      checkedText=""
                      uncheckedText=""
                    />
                  )}
                />

                <ToggleSwitchLabel>精算データの自動作成を有効にする</ToggleSwitchLabel>
              </ToggleSwitchWrapper>
              <div>
                締め日の日付指定
                <Required />
              </div>
              <CheckBoxRow>
                <Controller
                  control={control}
                  name="isMidOfMonthSelected"
                  rules={{
                    validate: (value) => {
                      if (!watch('isEnabled')) return true;
                      if (!value && !watch('isEndOfMonthSelected'))
                        return '締め日を選択してください';
                      return true;
                    },
                  }}
                  render={({ field: { value, onChange } }) => (
                    <CheckBox
                      checked={value}
                      setChecked={onChange}
                      disabled={!watch('isEnabled')}
                    />
                  )}
                />
                毎月15日
              </CheckBoxRow>
              <CheckBoxRow>
                <Controller
                  control={control}
                  name="isEndOfMonthSelected"
                  render={({ field: { value, onChange } }) => (
                    <CheckBox
                      checked={value}
                      setChecked={onChange}
                      disabled={!watch('isEnabled')}
                    />
                  )}
                />
                毎月末
              </CheckBoxRow>
              <FormErrorMessage errors={errors} name="isMidOfMonthSelected" />

              <AutoOffsetSection>
                <ToggleSwitchWrapper>
                  <Controller
                    control={control}
                    name="isAutoOffsetEnabled"
                    render={({ field: { value, onChange } }) => (
                      <ToggleSwitch
                        checked={value}
                        onChange={onChange}
                        checkedText=""
                        uncheckedText=""
                      />
                    )}
                  />
                  <ToggleSwitchLabel>債権の自動相殺を有効にする</ToggleSwitchLabel>
                </ToggleSwitchWrapper>
                <AutoOffsetDescription>
                  マイナス精算（請求）を発行体の債権として繰越し、次回以降の支払精算から自動で相殺します（締め日が古い順・部分相殺対応）。
                  既定は無効です。自動精算スケジュールとは独立して設定できます。
                </AutoOffsetDescription>
              </AutoOffsetSection>
            </Grid.Item>

            <Grid.Divider />
          </FormGrid>
          {apiResult.message && (
            <ApiResult isError={apiResult.isError}>{apiResult.message}</ApiResult>
          )}
          <Button onClick={handleSubmit(onUpdateSetting)}>登録</Button>
        </>
      ) : (
        <>
          <SelectedPrivateMoneyDisplay
            highlightHeader
            allowedPrivateMoneys={allowedPrivateMoneys}
          />
          <UserPrivateMoneyNotAllowed />
        </>
      )}
    </PageWrapper>
  );
};

export default ClearingAutomatedSetting;
